import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

// Generic split-view mega menu. Reusable for any nav trigger.
// Props: { label, sections: [{ id, label, links: [{ label, to }] }] }
export function MegaMenu({ label, sections = [], tone = "default", anchorToBar = false }) {
  const onOrange = tone === "onOrange";
  const orangeOutline = tone === "orangeOutline";
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const timer = useRef(null);
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const openNow = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    timer.current = setTimeout(() => setOpen(false), 140);
  };

  const active = sections[activeIdx] || sections[0];

  return (
    <div
      className={anchorToBar ? "" : "relative"}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      data-testid={`mega-trigger-${slug}`}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`px-3 h-11 flex items-center gap-1 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-150 ${
          onOrange
            ? open
              ? "text-white bg-[#C2410C]"
              : "text-white/90 hover:text-white hover:bg-[#C2410C]"
            : orangeOutline
            ? open
              ? "text-white bg-[#EA580C]"
              : "text-[#EA580C] hover:text-white hover:bg-[#EA580C]"
            : `border-b-2 ${open ? "text-[#6043EF] border-[#6043EF]" : "text-[#52525B] border-transparent hover:text-[#09090B]"}`
        }`}
      >
        {label}
        <CaretDown size={11} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && sections.length > 0 && (
        <div
          className={`absolute ${anchorToBar ? "left-0 w-full" : "right-0 w-[720px]"} top-full z-50 flex max-w-[92vw] bg-white border border-zinc-200 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.25)] animate-fade-in`}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          data-testid={`mega-panel-${slug}`}
        >
          {/* Left: sections */}
          <div className={`${anchorToBar ? "w-40" : "w-56"} shrink-0 border-r border-zinc-100 py-2 bg-zinc-50/60`}>
            {sections.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onFocus={() => setActiveIdx(i)}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between ${anchorToBar ? "text-xs" : "text-sm"} transition-colors ${
                  i === activeIdx
                    ? "bg-white text-[#6043EF] font-semibold border-l-2 border-[#6043EF]"
                    : "text-[#3f3f46] border-l-2 border-transparent hover:bg-white"
                }`}
                data-testid={`mega-section-${slug}-${s.id}`}
              >
                <span className="truncate">{s.label}</span>
                <CaretRight size={11} weight="bold" className={i === activeIdx ? "text-[#6043EF]" : "text-zinc-300"} />
              </button>
            ))}
          </div>

          {/* Right: SEO links of active section */}
          <div className={`flex-1 min-w-0 p-2.5 grid ${anchorToBar ? "grid-cols-1 gap-y-0" : "grid-cols-2 gap-x-3 gap-y-0.5"} content-start`}>
            {active?.links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`px-2.5 py-1.5 ${anchorToBar ? "text-xs" : "text-sm"} text-[#52525B] hover:text-[#6043EF] hover:bg-[#6043EF]/5 rounded transition-colors truncate`}
                data-testid={`mega-link-${slug}`}
                title={l.label}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
